import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import SiGithub from '@icons-pack/react-simple-icons/icons/SiGithub'
import SiX from '@icons-pack/react-simple-icons/icons/SiX'
import { ShieldCheck } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { FeedbackDialog } from '@/components/generals/feedback-dialog'
import { navLinks, siteConfig } from '@/constants/site'

const linkClass = 'rounded-md text-sm text-muted-foreground transition-colors outline-none hover:text-primary focus-visible:ring-4 focus-visible:ring-ring/30'

const socialClass = 'grid size-10 place-items-center rounded-xl border border-border bg-background text-muted-foreground transition-colors outline-none hover:border-primary/30 hover:bg-accent hover:text-primary focus-visible:ring-4 focus-visible:ring-ring/30'

export const Footer: FC = (): ReactNode => {
    return (
        <footer className='border-t border-border/70 bg-card'>
            <div className='mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr]'>
                <div className='flex max-w-sm flex-col gap-4'>
                    <Logo />

                    <p className='text-sm leading-relaxed text-muted-foreground'>
                        Save videos from TikTok, Instagram, X, Facebook and Pinterest in seconds. Free and unlimited, right in your browser.
                    </p>

                    <ul className='flex items-center gap-2'>
                        <li>
                            <a
                                href={siteConfig.links.x}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={socialClass}
                            >
                                <SiX title='' aria-hidden={true} className='size-4' />

                                <span className='sr-only'>
                                    CaveDL on X
                                </span>
                            </a>
                        </li>

                        <li>
                            <a
                                href={siteConfig.links.github}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={socialClass}
                            >
                                <SiGithub title='' aria-hidden={true} className='size-4' />

                                <span className='sr-only'>
                                    CaveDL on GitHub
                                </span>
                            </a>
                        </li>
                    </ul>
                </div>

                <nav aria-label='Site'>
                    <h2 className='mb-4 text-sm font-semibold text-foreground'>
                        CaveDL
                    </h2>

                    <ul className='flex flex-col gap-3'>
                        <li>
                            <Link href='/' className={linkClass}>
                                Download a video
                            </Link>
                        </li>

                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <Link href={link.href} className={linkClass}>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <nav aria-label='Help'>
                    <h2 className='mb-4 text-sm font-semibold text-foreground'>
                        Help
                    </h2>

                    <ul className='flex flex-col gap-3'>
                        <li>
                            <a
                                href={siteConfig.links.telegramBot}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={linkClass}
                            >
                                Telegram bot
                            </a>
                        </li>

                        <li>
                            <FeedbackDialog className={`${linkClass} cursor-pointer`}>
                                Send feedback
                            </FeedbackDialog>
                        </li>

                        <li>
                            <Link href='/privacy' className={linkClass}>
                                Privacy policy
                            </Link>
                        </li>
                    </ul>
                </nav>
            </div>

            <div className='border-t border-border/70'>
                <div className='mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 text-sm text-muted-foreground sm:px-6 md:flex-row md:items-center md:justify-between'>
                    <p className='flex items-start gap-2 md:items-center'>
                        <ShieldCheck className='mt-0.5 size-4 shrink-0 text-primary md:mt-0' aria-hidden='true' />
                        Only download content you own or have permission to use.
                    </p>

                    <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
                        <span>
                            Made by
                            {' '}
                            <span className='font-semibold text-foreground'>
                                {siteConfig.creator}
                            </span>
                        </span>

                        <span suppressHydrationWarning>
                            &copy; {new Date().getFullYear()} CaveDL
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    )
}
